import { useSelector } from 'react-redux';
import { useRewardsWheel } from '@/hooks/useRewardsWheel';
import { useEffect } from 'react';
import { selectAuthIsRegistration, selectIsAuthenticated } from '@/store/selectors/authSelectors';
import StorageManager from '@/shared/storageManager';
import { DateTime } from 'luxon';
import { selectIsMarketingConsented } from '@/store/selectors/consentsSelectors';
import { useModal } from '@/features/modal/useModal';
import { MODALS } from '@/features/modal/types';
import { envIsCanada, envIsNormal } from '@/constants/constants';
import { selectLocale } from '@/store/selectors/commonSelectors';

const has24HoursPassedSinceLastReminder = (): boolean => {
  const lastReminder = StorageManager.getRewardsWheelReminder();
  if (!lastReminder) {
    // if there is no last reminder in local storage we treat it as if it has been 24 hours
    return true;
  }
  const lastReminderTime = DateTime.fromISO(lastReminder, { zone: 'utc' });
  const now = DateTime.utc();

  return now.diff(lastReminderTime, 'hours').hours >= 24;
};

const shouldShowAgain = (marketingConsented?: boolean) => {
  const lastReminder = StorageManager.getRewardsWheelReminder();
  if (!lastReminder) {
    // if there is no last reminder in local storage we treat it as if user has not already closed the reminder
    return true;
  }
  if (lastReminder && !marketingConsented && envIsCanada()) {
    // If there is last reminder and user does not accept marketing consents, do not show anymore
    return false;
  }

  return true;
};

export function useRewardsWheelReminder(): void {
  const { isEnabled: isRewardReelEnabled } = useRewardsWheel();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isRegistration = useSelector(selectAuthIsRegistration);
  const locale = useSelector(selectLocale);
  const { openModal } = useModal();
  const marketingConsent = useSelector(selectIsMarketingConsented);
  const isRoc = envIsNormal() && ['ca', 'ca-fr'].includes(locale);

  useEffect(() => {
    if (
      isRewardReelEnabled &&
      !isRegistration &&
      isAuthenticated &&
      has24HoursPassedSinceLastReminder() &&
      shouldShowAgain(marketingConsent) &&
      !isRoc
    ) {
      openModal(MODALS.REWARDS_WHEEL_REMINDER);
    }
  }, [isRewardReelEnabled, isAuthenticated, isRegistration, marketingConsent]);
}
